/* Documentation
 * Visit https://capintel.atlassian.net/l/c/XdGPyg3n if you want a flow diagram of how the portfolio editor works!
 * */
// Third-party
import round from 'lodash/round';
import flatMap from 'lodash/flatMap';

// Enums
import { HoldingAllocationMode } from '@/enums/Portfolio';

// Redux
import { Action, Result, ApiAction, AsyncResult } from '../index';
import api from '../redux-api';
import { ReduxState } from './types';
import { togglePortfolioEditor } from './common/actionCreators';
import { cacheFundsData, getFundData, getManyFundsData } from './funds/fund-store';
import { getBenchmarkData, getFundsBenchmarks } from './benchmarks/benchmark-store';
import { getStockData, getManyStocksData } from './stocks/stock-store';
import { getStocksBenchmarks } from './comparison-benchmarks/comparison-benchmarks-store';
import { startLoaderAction, stopLoaderAction } from './ui/loaders';
import { clearSuitabilityAnalysis } from './suitability-analysis';

// Utils
import { portfolioToComparisonItem, getCurrentStateComparison, getMonthEndDataOption, shouldUseMonthlyRollingInReturns } from '../../utils/comparison';
import { deepClone } from '../../utils/objects';
import {
  formatPortfolioForSave,
  HOLDING_TYPES,
  splitPortfolioHoldings,
  generateCustomYieldHolding,
  setDefaultAllocationMode,
  PortfolioData,
  generateLocalizedNameForPortfolio,
  PortfolioEditorContext,
  normalizeHoldingNamesForUSLocale,
} from '@/utils/portfolios';
import calculatePortfolioStats, { recalculatePortfolioAllocations } from '../../utils/portfolio/calculate';
import calculatePerformance from '../../utils/portfolio/performance';
import { formatMessage, formatMessageWithLocale, LOCALE } from '../../utils/i18n';
import { comparisonBenchmarkToBenchmark } from '../../utils/comparison/benchmarks';
import { CURRENCY } from '../../utils/currency';
import { generateBSONId, generateTempId } from '../../utils/random';
import { portfolioHoldingsToComparisonHoldings, rollingPerformance, CalculatePortfolioOptions } from '../../utils/portfolio/utils';
import { getCachedCustomYieldFund, clearCustomYieldCache } from '@/utils/portfolio/customYieldCache';
import {
  calculateRiskMetrics,
  getCommonTimestamps,
  getDollarsAmountFromUnits,
  getUnitsFromDollarsAmount,
  addPortfolioHoldingsAllocations,
} from '../../utils/calculation-utils';
import {
  calculateHoldingDollarsFromWeight,
  calculateHoldingWeightsFromDollars,
  calculateTotalSizeOfHoldings,
  isCapitalGainsTaxableHolding,
} from '@capintel/instruments-lib';
import { addShelvesToPortfolioHoldings } from '../../utils/shelf-management';
import { sum } from '../../utils/math';
import { transformFDC3Portfolio } from '@/redux/modules/portfolios';
import sentryUtils from '@/utils/3rd-party/sentry';
import { canAccessAIStatementReader, canAccessTaxAnalysis } from '@/utils/feature-access';
import { isUserInLotRegion } from '@/utils/tax-analysis';
import { validateUserAppRegion } from '@/utils/users';

// Types
import { ADD_PORTFOLIOS_TO_COMPARISON } from './comparison';
import { EditPortfolioState, PortfolioBuilderContentOption, PortfolioCalculationPromiseReturn } from './types/edit-portfolio';
import { Interval } from '../../utils/constants';
import { selectCurrentTaxRate } from './taxes/selectors';
import { createCancellablePromise, withLoaderAction } from '../../utils/promise';
import { PERMISSIONS, userHasPermission } from '../../utils/permissions';
import { logAddToPortfolio, logRemoveFromPortfolio } from '../../services/usage-log';
import Nullable from '@/types/nullable';
import CancellablePromise from '@/types/cancellable-promise';
import AppRegion from '@/types/app-region';
import ComparisonHolding from '@/types/comparison-holding';
import GenericContact from '@/types/generic-contact';
import Fund from '@/types/fund';
import Portfolio from '@/types/portfolio';
import PortfolioRiskProfile from '@/types/portfolio-risk-profile';
import ComparisonItem from '@/types/comparison-item';
import CustomYieldParams from '@/types/custom-yield-params';
import ShelvableInstrument from '@/types/shelvable-instrument';
import CompliantInstrument from '@/types/compliant-instrument';
import PortfolioHolding from '@/types/portfolio-holding';
import Holding from '@/types/holding';
import { CsvFormat } from '@/views/components/modals/portfolio-upload/file-uploader/FileUploaderFlow';
import CommonInception from '@/types/common-inception';
import { recalcPortfolioItem } from './comparison/item-calculation';
import { PortfolioFDC3 } from '@/types/portfolio-fdc3';
import { resetProcessing } from './ai-statement-reader';
import { getAltData, getManyAltsData } from './alts/alts-store';
import AppMode from '@/types/app-mode';
import LocalizedString from '@/types/localized-string';
import helpers from '@/utils/disclaimers/helpers';
import { Account } from '@/types/planning';
import Shelf, { ShelfInfo } from '@/types/shelf';
import User from '@/types/user';
import { ApiError } from '../api';

// Selectors:
import { selectCustomProxies } from '@/redux/modules/custom-proxies/selectors';

export const INITIAL_STATE: EditPortfolioState = {
  portfolio: {
    name: {
      [LOCALE.EN_CA]: '',
      [LOCALE.FR_CA]: '',
    },
    holdings: [],
    size: 100000,
    dates: {},
    allocationMode: HoldingAllocationMode.Weight,
  } as unknown as Portfolio,
  contentOption: PortfolioBuilderContentOption.QuickSearch,
  holdingToCompareComplianceAlts: undefined,
  isSaving: false,
  isDirty: false,
  isLoading: false,
  isCalculating: false,
  isCreatingModel: undefined,
  editorContext: undefined,
  isPortfolioValueLocked: false,
  showTaxDetails: false,
  onCancel: () => {},
};

// Actions
const SET_PORTFOLIO_HOLDINGS: Action = {
  type: 'connect/edit-portfolio/funds/set',
};

const ADD_HOLDING: Action = {
  type: 'connect/edit-portfolio/holdings/add',
};

const CLEAR_PRODUCT_ADDED: Action = {
  type: 'connect/edit-portfolio/holdings/clear-product-added',
};

const REPLACE_HOLDING: Action = {
  type: 'connect/edit-portfolio/holdings/replace',
};

const REMOVE_HOLDING: Action = {
  type: 'connect/edit-portfolio/holdings/remove',
};

const REMOVE_ALL_HOLDINGS: Action = {
  type: 'connect/edit-portfolio/holdings/remove-all',
};

const REMOVE_PORTFOLIO_PLAN_ACCOUNT: Action = {
  type: 'connect/edit-portfolio/holdings/remove-portfolio-plan-account',
};

const UPDATE_HOLDING: Action = {
  type: 'connect/edit-portfolio/holdings/update',
};

export const SET_PORTFOLIO_PLAN_ACCOUNT: Action = {
  type: 'connect/portfolio/set-portfolio-plan-account',
};

const SET_NAME: Action = {
  type: 'connect/edit-portfolio/name',
};

const SAVE_PORTFOLIO: ApiAction = {
  type: 'connect/edit-portfolio/save',
  endpoint: 'portfolios/',
};

const SET_DISCRETIONARY_MER: Action = {
  type: 'connect/edit-portfolio/set-discretionary-mer',
};

const RESET_PORTFOLIO: Action = {
  type: 'connect/edit-portfolio/reset',
};

export const SET_PORTFOLIO: Action = {
  type: 'connect/edit-portfolio/init',
};

export const SET_PORTFOLIO_LOADING: Action = {
  type: 'connect/edit-portfolio/loading',
};

const SET_PORTFOLIO_IS_CALCULATING: Action = {
  type: 'connect/edit-portfolio/set-is-calculating',
};

const SET_PORTFOLIO_CAN_SAVE: Action = {
  type: 'connect/edit-portfolio/set-can-save',
};

const CALCULATE_PORTFOLIO: Action = {
  type: 'connect/edit-portfolio/calculate',
};

const SET_PORTFOLIO_CONTACT: Action = {
  type: 'connect/edit-portfolio/set-contact',
};

const SET_PORTFOLIO_ENTERPRISE: Action = {
  type: 'connect/edit-portfolio/set-enterprise',
};

const REGISTER_ON_CANCEL: Action = {
  type: 'connect/edit-portfolio/register-on-cancel',
};

const SET_COMPARISON_ITEM_NAME: Action = {
  type: 'connect/edit-portfolio/set-comparison-item-name',
};

const SET_COMPARISON_ITEM: Action = {
  type: 'connect/edit-portfolio/expand-compared-item',
};

const SET_RISK_PROFILE: Action = {
  type: 'connect/edit-portfolio/attach-risk-profile',
};

const SET_PORTFOLIO_ALLOCATION_MODE: Action = {
  type: 'connect/edit-portfolio/set-allocation-mode',
};

const SET_PORTFOLIO_CONTENT_OPTION: Action = {
  type: 'connect/edit-portfolio/set-portfolio-content-option',
};

export const ADD_FUNDS_TO_PORTFOLIO = {
  type: 'connect/edit-portfolio/add-funds-to-portfolio',
};

const SET_HOLDING_TO_REPLACE: Action = {
  type: 'connect/edit-portfolio/set-holding-to-replace',
};

const SET_HOLDING_TO_COMPARE_COMPLIANCE_ALTS: Action = {
  type: 'connect/edit-portfolio/set-holding-to-compare-compliance-alts',
};

export const SET_IS_ATTACHING_PROFILE_TO_PORTFOLIO: Action = {
  type: 'connect/edit-portfolio/set-is-attaching-profile-to-portfolio',
};

export const FETCH_HOLDINGS: ApiAction = {
  type: 'connect/edit-portfolio/fetch-holdings',
  endpoint: 'instruments/by-code/',
};

export const ADD_HOLDINGS_TO_PORTFOLIO_FROM_CSV: Action = {
  type: 'connect/edit-portfolio/add-holdings-to-portfolio-from-csv',
};

export const BATCH_ADD_HOLDINGS_TO_PORTFOLIO: Action = {
  type: 'connect/edit-portfolio/batch-add-holdings-to-portfolio',
};

const SET_PORTFOLIO_EDITOR_CONTEXT: Action = {
  type: 'connect/edit-portfolio/set-portfolio-editor-context',
};

export const SET_PORTFOLIO_EDITOR_IS_CREATING_MODEL: Action = {
  type: 'connect/edit-portfolio/set-portfolio-editor-is-creating-model',
};

const TOGGLE_PORTFOLIO_VALUE_LOCKED: Action = {
  type: 'connect/edit-portfolio/toggle-portfolio-value-locked',
};

const SET_SHOW_TAX_DETAILS: Action = {
  type: 'connect/edit-portfolio/set-show-tax-details',
};

const SET_NEW_PORTFOLIO_ID: Action = {
  type: 'connect/edit-portfolio/set-new-portfolio-id',
};

// Reducer
export default function reducer(state: EditPortfolioState = INITIAL_STATE, action: Action) {
  switch (action.type) {
    case SET_PORTFOLIO_HOLDINGS.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          holdings: action.holdings,
          size: action.size || state.portfolio.size,
        },
        isDirty: true,
      };
    }
    case SET_NAME.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          name: action.portfolioName,
        },
        isDirty: true,
      };
    }
    case SET_COMPARISON_ITEM_NAME.type: {
      const name = action.name || formatMessage('HoldingsEditor.NewName.PORTFOLIO');

      return {
        ...state,
        comparisonItem: {
          ...state.comparisonItem,
          name: {
            [LOCALE.EN_CA]: name,
            [LOCALE.FR_CA]: name,
          },
        },
      };
    }
    case SET_COMPARISON_ITEM.type: {
      return {
        ...state,
        comparisonItem: action.item,
      };
    }
    case RESET_PORTFOLIO.type: {
      return INITIAL_STATE;
    }
    case SET_PORTFOLIO_LOADING.type: {
      return {
        ...state,
        isLoading: true,
      };
    }
    case SET_PORTFOLIO.type: {
      return {
        ...state,
        isLoading: false,
        portfolio: action.portfolio,
        comparisonItem: action.comparisonItem,
        // If isDirty is explicitly dispatched, use that value - otherwise keep state value.
        isDirty: action.isDirty != null ? action.isDirty : state.isDirty,
      };
    }
    case SET_PORTFOLIO_CONTACT.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          contact: action.contact,
        },
      };
    }
    case SET_PORTFOLIO_ENTERPRISE.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          enterprise: action.enterpriseId,
        },
      };
    }
    case SET_PORTFOLIO_IS_CALCULATING.type: {
      return {
        ...state,
        isCalculating: action.isCalculating,
        calculationPromise: action.calculationPromise,
      };
    }
    case SET_PORTFOLIO_CAN_SAVE.type: {
      return {
        ...state,
        canSave: action.canSave,
      };
    }
    case REMOVE_PORTFOLIO_PLAN_ACCOUNT.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          planning: {},
        },
        isDirty: true,
      };
    }
    case SET_PORTFOLIO_PLAN_ACCOUNT.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          planning: {
            planId: action.payload.planId,
            planAccountId: action.payload.planAccountId,
            planAccount: action.payload.planAccount,
          },
        },
        isDirty: true,
      };
    }
    case `${CALCULATE_PORTFOLIO.type}_SUCCESS`: {
      return {
        ...state,
        portfolio: {
          ...action.portfolio,
          discretionaryMer: state.portfolio.discretionaryMer,
          size: action.refreshHoldings ? action.portfolio.size : state.portfolio.size,
          holdings: action.refreshHoldings ? action.portfolio.holdings : state.portfolio.holdings,
        },
        comparisonItem: action.comparisonItem,
      };
    }
    case ADD_HOLDING.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          holdings: [action.holding, ...state.portfolio.holdings],
        },
        isDirty: true,
      };
    }
    case CLEAR_PRODUCT_ADDED.type: {
      return {
        ...state,
        productAdded: undefined,
      };
    }
    case REPLACE_HOLDING.type: {
      const holdings = deepClone(state.portfolio.holdings);
      const toReplace = holdings.find(h => h._id === action.idToReplace);
      let productAdded;

      if (toReplace !== undefined) {
        // Merge tax info from the replaced holding, but preserve any user-edited values on the new holding
        const mergedHolding = {
          ...action.holding,
          taxSettings: action.holding.taxSettings || toReplace.taxSettings,
          lots: action.holding.lots || toReplace.lots,
          units: action.holding.units || toReplace.units,
        };

        holdings[holdings.indexOf(toReplace)] = mergedHolding;
        productAdded = mergedHolding;

        if (action.showReplacedFirst) {
          holdings.sort((a, b) => {
            if (a._id === action.holding._id) return -1;
            if (b._id === action.holding._id) return 1;
            return 0;
          });
        }
      }

      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          holdings,
        },
        productAdded,
        isDirty: true,
      };
    }
    case REMOVE_HOLDING.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          holdings: state.portfolio.holdings.filter(i => i._id !== action._id),
        },
        isDirty: true,
      };
    }
    case REMOVE_ALL_HOLDINGS.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          holdings: [],
        },
        isDirty: true,
      };
    }
    case UPDATE_HOLDING.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          holdings: state.portfolio.holdings.map(holding => {
            if (holding._id === action.holding._id) {
              return action.holding;
            }

            return holding;
          }),
        },
        isDirty: true,
      };
    }
    case SET_DISCRETIONARY_MER.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          discretionaryMer: action.mer,
        },
        isDirty: true,
      };
    }
    case SAVE_PORTFOLIO.type: {
      return {
        ...state,
        isSaving: true,
      };
    }
    case `${SAVE_PORTFOLIO.type}_SUCCESS`:
    case `${SAVE_PORTFOLIO.type}_ERROR`: {
      return {
        ...state,
        isSaving: false,
        isDirty: false,
      };
    }
    case REGISTER_ON_CANCEL.type: {
      return {
        ...state,
        onCancel: action.handler,
      };
    }
    case SET_HOLDING_TO_REPLACE.type: {
      return {
        ...state,
        holdingToReplace: action.holdingToReplace,
      };
    }
    case SET_HOLDING_TO_COMPARE_COMPLIANCE_ALTS.type: {
      return {
        ...state,
        holdingToCompareComplianceAlts: action.holdingToCompare,
      };
    }
    case SET_RISK_PROFILE.type: {
      const { profile: riskProfile } = action;

      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          riskProfile,
          riskProfileRef: riskProfile != null ? riskProfile.profile.key : undefined,
        },
        comparisonItem: {
          ...state.comparisonItem,
          riskProfile: riskProfile != null ? riskProfile.profile : undefined,
        },
        isDirty: true,
      };
    }
    case SET_PORTFOLIO_ALLOCATION_MODE.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          allocationMode: action.allocationMode,
        },
      };
    }
    case SET_PORTFOLIO_CONTENT_OPTION.type: {
      return {
        ...state,
        contentOption: action.option,
      };
    }
    case SET_PORTFOLIO_EDITOR_CONTEXT.type: {
      return {
        ...state,
        editorContext: action.editorContext,
      };
    }
    case SET_PORTFOLIO_EDITOR_IS_CREATING_MODEL.type: {
      return {
        ...state,
        isCreatingModel: action.isCreatingModel,
      };
    }
    case TOGGLE_PORTFOLIO_VALUE_LOCKED.type: {
      return {
        ...state,
        isPortfolioValueLocked: !state.isPortfolioValueLocked,
      };
    }
    case SET_SHOW_TAX_DETAILS.type: {
      return {
        ...state,
        showTaxDetails: action.payload,
      };
    }
    case SET_NEW_PORTFOLIO_ID.type: {
      const newId = generateTempId();

      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          _id: newId,
          correlationId: newId.replace('new-', ''),
        },
      };
    }
    default:
      return state;
  }
}

// Action Creators
export const resetPortfolio =
  (keepModalOpen: boolean = false): Result<void> =>
  (dispatch, getState) => {
    const state = getState();
    const {
      auth: { user },
      editPortfolio: { portfolio },
    } = state;

    // Clear custom yield cache when resetting portfolio
    clearCustomYieldCache();

    if (!keepModalOpen) {
      dispatch(togglePortfolioEditor(false));
    }
    dispatch(RESET_PORTFOLIO);

    if (canAccessAIStatementReader(user)) {
      dispatch(resetProcessing());
    }
  };

export const savePortfolio =
  (portfolio: Portfolio, contact?: GenericContact, enterpriseGroupId?: string): AsyncResult<Portfolio> =>
  (dispatch, getState) => {
    if (portfolio.holdings.some(h => h.weight === 0 || h.dollars === 0)) {
      return Promise.reject({ errorKey: 'PORTFOLIO_EMPTY_HOLDING' });
    }

    // We need to normalize the names of the holdings to contain en-CA and fr-CA name when only en-US is available for US clients
    // This function mutates the portfolio object in-place.
    normalizeHoldingNamesForUSLocale(portfolio);

    const data: any = { ...portfolio };
    if (data._id && (data._id.startsWith('new-') || data.isFromPostMessage)) {
      delete data._id;
    }

    // If the portfolio was created from a post message but was a clone,
    // we clear the isFromPostMessage to not send it to the backend
    if (data.isCloned && data.isFromPostMessage) {
      delete data.isFromPostMessage;
    }

    if (contact) {
      data.contact = contact;
      data.mine = false;
    }

    if (enterpriseGroupId) {
      const {
        enterprise: { details },
      } = getState();
      data.enterpriseId = details ? details._id : null;
      data.enterpriseGroups = [enterpriseGroupId];
    }

    return api.post(SAVE_PORTFOLIO, null, formatPortfolioForSave(data)).then((res: { portfolio: any }) => {
      // Clear any remote suitability results that may have been saved
      dispatch(clearSuitabilityAnalysis());
      // Re-insert shelves information for each holding back into the backend response
      // The shelves information is used to derive the proper flag when the portfolio
      // is converted to a comparison item
      return {
        ...res.portfolio,
        holdings: portfolio.holdings,
      };
    });
  };

function mergeInstrumentDataIntoHoldings(holdings: PortfolioHolding[], data: PortfolioData): PortfolioHolding[] {
  const allInstruments = [...data.funds, ...data.stocks, ...data.alts, ...data.benchmarks];

  return holdings.map(holding => {
    const instrument = allInstruments.find(i => i._id === holding.original.id);

    if (instrument && 'nav' in instrument) {
      return { ...holding, nav: instrument.nav };
    }

    return holding;
  });
}

export const addCustomYieldHolding =
  (
    config: CustomYieldParams,
    shelfInfo: ShelvableInstrument & CompliantInstrument,
    currency: string,
    holdingToReplace?: PortfolioHolding | undefined,
    showReplacedFirst?: boolean,
  ): Result<void> =>
  (dispatch, getState) => {
    const holding = generateCustomYieldHolding(config, currency, shelfInfo);

    if (holdingToReplace) {
      holding.weight = holdingToReplace.weight;
      holding.dollars = holdingToReplace.dollars;
      holding.taxSettings = holdingToReplace.taxSettings;
      holding.lots = holdingToReplace.lots;
    }

    if (holdingToReplace != null) {
      dispatch({ type: REPLACE_HOLDING.type, holding, idToReplace: holdingToReplace._id, showReplacedFirst });
    } else {
      dispatch({ type: ADD_HOLDING.type, holding });
    }

    dispatch(logAddToPortfolio(holding, getState().editPortfolio.portfolio));
  };

export const getHoldingData =
  (holding: PortfolioHolding): AsyncResult<Nullable<Holding>> =>
  dispatch => {
    const holdingId = holding.original.id;
    if (!holdingId) {
      return Promise.resolve(null);
    }

    switch (holding.original.type) {
      case HOLDING_TYPES.FUND: {
        return dispatch(getFundData(holdingId));
      }
      case HOLDING_TYPES.STOCK: {
        return dispatch(getStockData(holdingId));
      }
      case HOLDING_TYPES.ALT: {
        return dispatch(getAltData(holdingId));
      }
      default:
        return Promise.resolve(null);
    }
  };

export const createOrAppendLotBasedHolding = (existingHolding: PortfolioHolding | undefined, holdingToAdd: PortfolioHolding): PortfolioHolding => {
  const newLot = {
    _id: holdingToAdd._id,
    units: 0,
    dollars: 0,
    costBasis: 0,
    purchaseDate: undefined,
  };
  // If no existing holding, create a new one with the new lot
  if (!existingHolding) {
    return {
      ...holdingToAdd,
      lots: [newLot],
      units: 0,
      dollars: 0,
    };
  }

  const existingLots = existingHolding.lots ?? [];
  // If lots already exist, just append the new lot
  if (existingLots.length > 0) {
    return {
      ...existingHolding,
      lots: [...existingLots, newLot],
    };
  }
  // If the holding does NOT yet use lots, convert the holding-level values into the first lot, then append the new lot
  const firstLot = {
    _id: existingHolding._id,
    units: existingHolding.units ?? 0,
    dollars: existingHolding.dollars ?? 0,
    costBasis: existingHolding.taxSettings?.costBasis ?? 0,
    purchaseDate: existingHolding.taxSettings?.purchaseDate,
  };

  return {
    ...existingHolding,
    lots: [firstLot, newLot],
    units: firstLot.units,
    dollars: firstLot.dollars,
  };
};

export const createOrAppendMergedHolding = (existingHolding: PortfolioHolding | undefined, holdingToAdd: PortfolioHolding): PortfolioHolding => {
  // If no existing holding, create a new one with zero allocation
  if (!existingHolding) {
    return {
      ...holdingToAdd,
      units: 0,
      dollars: 0,
    };
  }

  // For Canadian region: preserve existing holding's aggregated values
  // Multiple holdings of the same instrument are merged into a single entry
  return {
    ...existingHolding,
    units: existingHolding.units ?? 0,
    dollars: existingHolding.dollars ?? 0,
  };
};

export const addHoldingWithTax =
  (portfolio: Portfolio, holding: PortfolioHolding): AsyncResult<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      auth: { user },
    } = state;
    const existingHolding = portfolio.holdings.find(h => h.original?.id === holding.original?.id);

    // Apply region-specific tax structure
    const isRegionCA = validateUserAppRegion(user, AppRegion.CAN);
    const isRegionUS = validateUserAppRegion(user, AppRegion.US);

    let updatedHolding = holding;
    if (isRegionCA) {
      // Canadian region: use merged holdings structure
      updatedHolding = createOrAppendMergedHolding(existingHolding, holding);
    } else if (isRegionUS) {
      // US region: use lot-based structure
      updatedHolding = createOrAppendLotBasedHolding(existingHolding, holding);
    }

    // Update existing holding or add new one
    if (existingHolding) {
      await dispatch(updatePortfolioHolding(updatedHolding));
    } else {
      await dispatch({ type: ADD_HOLDING.type, holding: updatedHolding });
    }

    dispatch(logAddToPortfolio(updatedHolding, portfolio));
  };

export const addFundToPortfolioById =
  (id: string, holdingToReplace?: PortfolioHolding | undefined, showReplacedFirst?: boolean): AsyncResult<void> =>
  async (dispatch, getState) => {
    const fund = await dispatch(getFundData(id));
    const state = getState();
    const {
      auth: { user },
      editPortfolio: { showTaxDetails, portfolio },
    } = state;
    const isTaxAnalysisFeatureEnabled = canAccessTaxAnalysis(user);

    // Prevent adding duplicate holdings for Canada region users with tax details enabled
    const isRegionCA = validateUserAppRegion(user, AppRegion.CAN);
    const holdingExists = portfolio.holdings.some(h => h.original?.id === id);
    if (isTaxAnalysisFeatureEnabled && isRegionCA && showTaxDetails && holdingExists) {
      return Promise.reject({ errorKey: 'PORTFOLIO_TAX_DETAILS_DUPLICATE_HOLDING' });
    }

    const holding: PortfolioHolding = {
      _id: generateBSONId(),
      symbol: fund.symbols && fund.symbols[0], // TODO -- Allow user to pick a fund by code [CON-36]
      currency: fund.currency,
      original: {
        id: fund._id,
        type: HOLDING_TYPES.FUND,
      },
      name: fund.names?.full as LocalizedString,
      weight: holdingToReplace ? holdingToReplace.weight || 0 : 0,
      dollars: holdingToReplace ? holdingToReplace.dollars || 0 : 0,
      filteredShelves: fund.filteredShelves,
      complianceComparisons: fund.complianceComparisons,
      universe: fund.universe,
      taxSettings: holdingToReplace?.taxSettings,
      lots: holdingToReplace?.lots,
      nav: fund.nav,
    };

    // We're using the weight/dollars from the holding getting replaced - if available - to
    // calculate the units for that same holding. In the case where only the units would be
    // available on the replaced holding, we can't calculate anything at all since the price
    // of a unit varies based on the fund's price.
    const data = await dispatch(getHoldingData(holding));
    if (data) {
      holding.units = getUnitsFromDollarsAmount(holding.original.type, holding.dollars, data);
    }

    // We need to add a special check when we add fund to portfolio as bonds are also type fund and check if we have nav information (Cash and other edge cases doesn't contain nav)
    const isInLotRegion = isUserInLotRegion(user);
    const isTaxable = isTaxableHolding(holding, data);
    const shouldEnableTaxTracking = isTaxAnalysisFeatureEnabled && showTaxDetails && isTaxable;
    const shouldCreateLot = shouldEnableTaxTracking && isInLotRegion;

    if (holdingToReplace != null) {
      await dispatch({ type: REPLACE_HOLDING.type, holding, idToReplace: holdingToReplace._id, showReplacedFirst });
    } else if (shouldEnableTaxTracking || shouldCreateLot) {
      await dispatch(addHoldingWithTax(portfolio, holding));
    } else {
      await dispatch({ type: ADD_HOLDING.type, holding });
    }

    dispatch(logAddToPortfolio(holding, getState().editPortfolio.portfolio));
  };

export const addStockToPortfolioById =
  (id: string, holdingToReplace?: PortfolioHolding | undefined, showReplacedFirst?: boolean): AsyncResult<void> =>
  async (dispatch, getState) => {
    const stock = await dispatch(getStockData(id));
    const state = getState();
    const {
      auth: { user },
      editPortfolio: { showTaxDetails, portfolio },
    } = state;
    const isTaxAnalysisFeatureEnabled = canAccessTaxAnalysis(user);

    // Prevent adding duplicate holdings for Canada region users with tax details enabled
    const isRegionCA = validateUserAppRegion(user, AppRegion.CAN);
    const holdingExists = portfolio.holdings.some(h => h.original?.id === id);
    if (isTaxAnalysisFeatureEnabled && isRegionCA && showTaxDetails && holdingExists) {
      return Promise.reject({ errorKey: 'PORTFOLIO_TAX_DETAILS_DUPLICATE_HOLDING' });
    }

    const holding: PortfolioHolding = {
      _id: generateBSONId(),
      symbol: stock.ticker,
      currency: stock.currency,
      original: {
        id: stock._id,
        type: HOLDING_TYPES.STOCK,
      },
      name: {
        [LOCALE.EN_CA]: stock.companyName,
      },
      weight: holdingToReplace ? holdingToReplace.weight || 0 : 0,
      dollars: holdingToReplace ? holdingToReplace.dollars || 0 : 0,
      filteredShelves: stock.filteredShelves,
      complianceComparisons: stock.complianceComparisons,
      taxSettings: holdingToReplace?.taxSettings,
      lots: holdingToReplace?.lots,
      nav: stock.nav,
    };

    // See comment in addFundToPortfolioById().
    const data = await dispatch(getHoldingData(holding));
    if (data) {
      holding.units = getUnitsFromDollarsAmount(holding.original.type, holding.dollars, data);
    }

    const isInLotRegion = isUserInLotRegion(user);
    const isTaxable = isTaxableHolding(holding, data);
    const shouldEnableTaxTracking = isTaxAnalysisFeatureEnabled && showTaxDetails && isTaxable;
    const shouldCreateLot = shouldEnableTaxTracking && isInLotRegion;

    if (holdingToReplace != null) {
      await dispatch({ type: REPLACE_HOLDING.type, holding, idToReplace: holdingToReplace._id, showReplacedFirst });
    } else if (shouldEnableTaxTracking || shouldCreateLot) {
      await dispatch(addHoldingWithTax(portfolio, holding));
    } else {
      await dispatch({ type: ADD_HOLDING.type, holding });
    }

    dispatch(logAddToPortfolio(holding, getState().editPortfolio.portfolio));
  };

export const addAltToPortfolioById =
  (id: string, holdingToReplace?: PortfolioHolding | undefined, showReplacedFirst?: boolean): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { _id, currency, filteredShelves, names } = await dispatch(getAltData(id));

    const holding: PortfolioHolding = {
      _id: generateBSONId(),
      symbol: null,
      currency,
      original: {
        id: _id,
        type: HOLDING_TYPES.ALT,
      },
      name: names?.full!,
      weight: holdingToReplace?.weight || 0,
      dollars: holdingToReplace?.dollars || 0,
      filteredShelves,
      taxSettings: holdingToReplace?.taxSettings,
      lots: holdingToReplace?.lots,
    };

    if (holdingToReplace != null) {
      await dispatch({ type: REPLACE_HOLDING.type, holding, idToReplace: holdingToReplace._id, showReplacedFirst });
    } else {
      await dispatch({ type: ADD_HOLDING.type, holding });
    }

    dispatch(logAddToPortfolio(holding, getState().editPortfolio.portfolio));
  };

export const allocateRemaining = (): Result<void> => async (dispatch, getState) => {
  const { portfolio } = getState().editPortfolio;

  const holdings = deepClone(portfolio.holdings);
  const emptyHoldings = holdings.filter(h => h.weight === 0);
  const unallocatedHoldings = emptyHoldings.length ? emptyHoldings : holdings;
  const unallocatedWeight = 100 - holdings.reduce((total, holding) => total + holding.weight, 0);
  const remainder = unallocatedWeight / unallocatedHoldings.length;
  const portfolioSize = portfolio.size || 0;

  const newHoldings = await Promise.all(
    holdings.map(
      holding =>
        new Promise(async resolve => {
          if (!unallocatedHoldings.some(h => h._id === holding._id)) {
            resolve(holding);
            return;
          }

          const newHolding = deepClone(holding);
          newHolding.weight += remainder;
          newHolding.dollars = round((portfolioSize * newHolding.weight) / 100, 2);

          const data = await dispatch(getHoldingData(holding));
          if (data) {
            newHolding.units = getUnitsFromDollarsAmount(newHolding.original.type, newHolding.dollars, data);
          }

          resolve(newHolding);
        }),
    ),
  );

  dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings: newHoldings });
};

export const allocateEvenly = (): AsyncResult<void> => async (dispatch, getState) => {
  const { portfolio } = getState().editPortfolio;

  const remainder = 100 / portfolio.holdings.length;
  const portfolioSize = portfolio.size || 0;

  const holdings = await Promise.all(
    portfolio.holdings.map(
      holding =>
        new Promise(async resolve => {
          const newHolding = deepClone(holding);
          newHolding.weight = remainder;
          newHolding.dollars = round((portfolioSize * newHolding.weight) / 100, 2);

          const data = await dispatch(getHoldingData(holding));
          if (data) {
            newHolding.units = getUnitsFromDollarsAmount(newHolding.original.type, newHolding.dollars, data);
          }

          return resolve(newHolding);
        }),
    ),
  );

  dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings });
};

export const removeHoldingFromPortfolio =
  (holding: PortfolioHolding): Result<void> =>
  (dispatch, getState) => {
    dispatch({ type: REMOVE_HOLDING.type, _id: holding._id });

    const {
      editPortfolio: { portfolio },
    } = getState();

    // If allocating by units, the allocations shouldn't change - simply adjust the
    // total portfolio size to account for the removed holding.
    if (portfolio.allocationMode === HoldingAllocationMode.Units) {
      const newSize = (portfolio.size || 0) - holding.dollars;
      const newHoldings = calculateHoldingWeightsFromDollars(portfolio.holdings, newSize);
      dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings: newHoldings, size: newSize });
    }

    dispatch(logRemoveFromPortfolio(holding, portfolio));
  };

export const removeAllHoldingsFromPortfolio = (): Result<void> => dispatch => {
  dispatch({ type: REMOVE_ALL_HOLDINGS.type });
};

export const updatePortfolioHolding =
  (holding: PortfolioHolding): Result<void> =>
  dispatch => {
    dispatch({ type: UPDATE_HOLDING.type, holding });
  };

export const changeHoldingSymbol =
  (id: string, symbol: string): Result<void> =>
  (dispatch, getState) => {
    const { portfolio } = getState().editPortfolio;

    const holdings = portfolio.holdings.map(holding => {
      if (holding._id !== id) {
        return holding;
      }

      return {
        ...holding,
        symbol,
      };
    });

    dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings });
  };

export const changeHoldingWeight =
  (id: string, weight: number, data: Holding): Result<void> =>
  (dispatch, getState) => {
    const { portfolio } = getState().editPortfolio;

    const holdings = portfolio.holdings.map(holding => {
      if (holding._id !== id) {
        return holding;
      }

      const newHolding = deepClone(holding);
      newHolding.weight = weight;
      newHolding.dollars = calculateHoldingDollarsFromWeight(weight, portfolio.size);
      newHolding.units = getUnitsFromDollarsAmount(newHolding.original.type, newHolding.dollars, data);

      return newHolding;
    });

    dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings });
  };

export const changeHoldingDollars =
  (id: string, dollars: number, data: Holding): Result<void> =>
  (dispatch, getState) => {
    const { portfolio, isPortfolioValueLocked } = getState().editPortfolio;

    let size = portfolio.size || 0;

    let holdings = portfolio.holdings.map(holding => {
      if (holding._id !== id) {
        return holding;
      }

      const newHolding = deepClone(holding);
      newHolding.dollars = +dollars.toFixed(2);
      newHolding.units = getUnitsFromDollarsAmount(newHolding.original.type, dollars, data);

      return newHolding;
    });

    // Only recalculate portfolio size if value is not locked and user doesn't have permission
    if (!(isPortfolioValueLocked && userHasPermission(getState().auth.user, PERMISSIONS.PORTFOLIO_EDITOR_LOCK_TOTAL_VALUE))) {
      size = round(holdings.map(holding => holding.dollars).reduce(sum, 0), 2);
    }

    // Calculate new holding weights from dollars
    holdings = calculateHoldingWeightsFromDollars(holdings, size);

    dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings, size });
  };

export const changeHoldingUnits =
  (id: string, units: number, data: Holding): Result<void> =>
  (dispatch, getState) => {
    const { portfolio, isPortfolioValueLocked } = getState().editPortfolio;

    let size = portfolio.size || 0;

    let holdings = portfolio.holdings.map(holding => {
      if (holding._id !== id) {
        return holding;
      }

      const newHolding = deepClone(holding);
      newHolding.units = units;
      newHolding.dollars = getDollarsAmountFromUnits(newHolding.original.type, units, data) || 0;

      return newHolding;
    });

    // Only recalculate portfolio size if value is not locked and user doesn't have permission
    if (!(isPortfolioValueLocked && userHasPermission(getState().auth.user, PERMISSIONS.PORTFOLIO_EDITOR_LOCK_TOTAL_VALUE))) {
      size = round(holdings.map(holding => holding.dollars).reduce(sum, 0), 2);
    }

    // Calculate new holding weights from dollars
    holdings = calculateHoldingWeightsFromDollars(holdings, size);

    dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings, size });
  };

export const changeHoldingCostBasis =
  (costBasis: number, holdingId: string, lotId?: string): Result<void> =>
  (dispatch, getState) => {
    const { portfolio } = getState().editPortfolio;

    const holdings = portfolio.holdings.map(holding => {
      // Check if this is the target holding
      if (holding._id !== holdingId) {
        return holding;
      }

      const newHolding = deepClone(holding);

      // If lotId is provided, update the specific lot
      if (lotId && newHolding.lots?.length) {
        newHolding.lots = newHolding.lots.map(lot => {
          if (lot._id === lotId) {
            return {
              ...lot,
              costBasis,
            };
          }
          return lot;
        });
      } else {
        // If no lotId, update the holding-level cost basis
        newHolding.taxSettings = {
          ...newHolding.taxSettings,
          costBasis,
        };
      }

      return newHolding;
    });

    dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings });
  };

export const changeHoldingPurchaseDate =
  (purchaseDate: Date | null, holdingId: string, lotId?: string): Result<void> =>
  (dispatch, getState) => {
    const { portfolio } = getState().editPortfolio;

    const holdings = portfolio.holdings.map(holding => {
      // Check if this is the target holding
      if (holding._id !== holdingId) {
        return holding;
      }

      const newHolding = deepClone(holding);

      // If lotId is provided, update the specific lot
      if (lotId && newHolding.lots?.length) {
        newHolding.lots = newHolding.lots.map(lot => {
          if (lot._id === lotId) {
            return {
              ...lot,
              purchaseDate: purchaseDate === null ? undefined : purchaseDate,
            };
          }
          return lot;
        });
      } else {
        // If no lotId, update the holding-level purchase date
        newHolding.taxSettings = {
          ...newHolding.taxSettings,
          purchaseDate: purchaseDate === null ? undefined : purchaseDate,
        };
      }

      return newHolding;
    });

    dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings });
  };

export const changeHoldingLotUnits =
  (units: number, lotId: string, holdingId?: string): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { portfolio } = getState().editPortfolio;

    // Get price data for the holding
    const holding = portfolio.holdings.find(h => h._id === holdingId);
    if (!holding) {
      return;
    }

    const holdingsData = await dispatch(getPortfolioHoldingsData(portfolio));
    const data = [...holdingsData.funds, ...holdingsData.stocks, ...holdingsData.alts].find(h => h._id === holding.original.id);

    const holdings = portfolio.holdings.map(holding => {
      // Check if this is the parent holding that contains lots
      if (holding._id !== holdingId || !holding.lots?.length) {
        return holding;
      }

      const newHolding = deepClone(holding);

      // Find and update the specific lot by id, calculating dollars from units and price
      newHolding.lots = newHolding.lots!.map(lot => {
        if (lot._id === lotId) {
          const dollars = data ? getDollarsAmountFromUnits(holding.original.type, units, data) || 0 : lot.dollars;
          return {
            ...lot,
            units,
            dollars,
          };
        }
        return lot;
      });

      // Update holding's total units and dollars to be the sum of all lots
      const totalUnits = newHolding.lots.reduce((sum, lot) => sum + (lot.units || 0), 0);
      const totalDollars = newHolding.lots.reduce((sum, lot) => sum + (lot.dollars || 0), 0);
      newHolding.units = totalUnits;
      newHolding.dollars = totalDollars;

      return newHolding;
    });

    dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings });
  };

export const changeHoldingLotDollars =
  (dollars: number, lotId: string, holdingId?: string): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { portfolio } = getState().editPortfolio;

    const size = portfolio.size || 0;

    // Get price data for the holding
    const holding = portfolio.holdings.find(h => h._id === holdingId);
    if (!holding) {
      return;
    }

    const holdingsData = await dispatch(getPortfolioHoldingsData(portfolio));
    const data = [...holdingsData.funds, ...holdingsData.stocks, ...holdingsData.alts].find(h => h._id === holding.original.id);

    let holdings = portfolio.holdings.map(holding => {
      // Check if this is the parent holding that contains lots
      if (holding._id !== holdingId || !holding.lots?.length) {
        return holding;
      }

      const newHolding = deepClone(holding);

      // Find and update the specific lot by id, calculating units from dollars and price
      newHolding.lots = newHolding.lots!.map(lot => {
        if (lot._id === lotId) {
          const units = data ? getUnitsFromDollarsAmount(holding.original.type, dollars, data) || lot.units : lot.units;
          return {
            ...lot,
            dollars,
            units,
          };
        }
        return lot;
      });

      // Update holding's total dollars and units to be the sum of all lots
      const totalDollars = newHolding.lots.reduce((sum, lot) => sum + (lot.dollars || 0), 0);
      const totalUnits = newHolding.lots.reduce((sum, lot) => sum + (lot.units || 0), 0);
      newHolding.dollars = totalDollars;
      newHolding.units = totalUnits;

      return newHolding;
    });

    // Calculate new holding weights from dollars
    holdings = calculateHoldingWeightsFromDollars(holdings, size);

    dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings });
  };

export const recalculatePortfolioAllocation = (): Result<void> => (dispatch, getState) => {
  const { portfolio } = getState().editPortfolio;

  const size = round(calculateTotalSizeOfHoldings(portfolio.holdings), 2);

  const newHoldings = calculateHoldingWeightsFromDollars(portfolio.holdings, size);

  dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings: newHoldings, size });
};

export const recalculatePortfolioHoldings = (): AsyncResult<void> => async (dispatch, getState) => {
  const { portfolio } = getState().editPortfolio;

  // Fetch latest holding data for all holdings
  const holdingsData = await dispatch(getPortfolioHoldingsData(portfolio));

  // Recalculate dollars and units for each holding based on allocation mode
  let newHoldings = portfolio.holdings.map(holding => {
    const data = [...holdingsData.funds, ...holdingsData.stocks, ...holdingsData.alts].find(h => h._id === holding.original.id);
    if (!data) return holding;

    const newHolding = { ...holding };

    if (portfolio.allocationMode === HoldingAllocationMode.Units) {
      // Calculate dollars from units and price
      newHolding.dollars = getDollarsAmountFromUnits(newHolding.original.type, newHolding.units!, data) || 0;
    } else if (portfolio.allocationMode === HoldingAllocationMode.Weight) {
      // Calculate dollars from weight and portfolio size
      newHolding.dollars = calculateHoldingDollarsFromWeight(newHolding.weight, portfolio.size);
      // Calculate units from dollars and price
      newHolding.units = getUnitsFromDollarsAmount(newHolding.original.type, newHolding.dollars, data);
    }

    return newHolding;
  });

  // Recalculate portfolio size
  const size = round(newHoldings.map(h => h.dollars).reduce(sum, 0), 2);

  // Recalculate weights from dollars
  newHoldings = calculateHoldingWeightsFromDollars(newHoldings, size);

  dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings: newHoldings, size });
};

export const setPortfolioSize =
  (size: Nullable<number>, preserveHoldingDollars: boolean = false): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { portfolio } = getState().editPortfolio;

    const holdings = await Promise.all(
      portfolio.holdings.map(
        holding =>
          new Promise(async resolve => {
            const data = await dispatch(getHoldingData(holding));

            let dollars: number;
            let weight: number;

            if (preserveHoldingDollars) {
              // Preserve existing dollars and recalculate weight
              dollars = holding.dollars || 0;
              weight = size && size > 0 ? round((dollars / size) * 100, 2) : 0;
            } else {
              // Recalculate dollars based on weight
              dollars = round((holding.weight * (size || 0)) / 100, 2);
              weight = holding.weight;
            }

            let units: number | undefined;
            if (data) {
              units = getUnitsFromDollarsAmount(holding.original.type, dollars, data);
            }

            resolve({
              ...holding,
              dollars,
              weight,
              units,
            });
          }),
      ),
    );

    dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings, size });
  };

export const clearPortfolio = (): Result<void> => (dispatch, getState) => {
  const holdings = getState().editPortfolio.portfolio.holdings.map(holding => ({
    ...holding,
    weight: 0,
    dollars: 0,
  }));

  dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings, size: 0 });
};

export const clearHoldings = (): Result<void> => dispatch => {
  dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings: [], size: 0 });
};

export const setPortfolioDiscretionaryMer =
  (mer: number): Result<void> =>
  dispatch => {
    dispatch({ type: SET_DISCRETIONARY_MER.type, mer });
  };

export const setPortfolioName =
  (name: string): Result<void> =>
  (dispatch, getState) => {
    const state = getState();
    const {
      auth: { user },
    } = state;

    const portfolioName = user ? generateLocalizedNameForPortfolio(name, user) : name;

    dispatch({ type: SET_NAME.type, portfolioName });
  };

export const setNewPortfolioId = (): Result<void> => dispatch => {
  dispatch({ type: SET_NEW_PORTFOLIO_ID.type });
};

export const calculateCustomFund =
  (fund: Fund): AsyncResult<Fund> =>
  async dispatch => {
    const benchmarkId = fund.benchmarkLipperId;
    const benchmarkData = await dispatch(getBenchmarkData((benchmarkId && benchmarkId.toString()) || null));
    const benchmarkRolling = (benchmarkData && benchmarkData.performance.rolling.monthly) || [];
    const risk = calculateRiskMetrics(fund.performance.rolling.monthly, benchmarkRolling, benchmarkRolling);
    const lastTimestamp = getCommonTimestamps(
      [
        { weekly: [], monthly: benchmarkRolling },
        { weekly: [], monthly: fund.performance.rolling.monthly },
      ],
      'DESC',
    ).monthly;

    return {
      ...fund,
      performance: {
        ...fund.performance,
        ...risk,
      },
      dates: {
        ...fund.dates,
        risk: {
          endDate: new Date(lastTimestamp),
        },
      },
    };
  };

/**
 * Looks up the appropriate shelf and compliance info for a holding
 * based on the Redux state’s custom proxies.
 */
export function getShelfInfoForHolding(state: ReduxState, holding: PortfolioHolding): ShelfInfo {
  const customProxies = selectCustomProxies(state);
  const defaultInfo: ShelfInfo = {
    filteredShelves: holding.filteredShelves as Shelf[],
    complianceComparisons: holding.complianceComparisons,
  };

  if (!customProxies || typeof customProxies !== 'object') return defaultInfo;

  for (const proxyKey of Object.keys(customProxies)) {
    const proxies = customProxies[proxyKey];
    if (!Array.isArray(proxies)) continue;

    for (const proxy of proxies) {
      const storedProxyName = proxy?.names?.full?.['en-CA'];
      const holdingName = holding.original?.yieldParams?.name;

      if (storedProxyName && storedProxyName === holdingName) {
        return {
          filteredShelves: proxy?.details?.shelves ?? [],
          complianceComparisons: proxy?.complianceComparisons ?? [],
        };
      }
    }
  }

  return defaultInfo;
}

/**
 * Fetches and processes portfolio holdings data including funds, stocks, and custom yields.
 * It also fetched the benchmarks for each holding.
 *
 * {
 *  funds: Fund[],
 *  stocks: Stock[],
 *  benchmarks: Benchmark[]
 * }
 *
 * @param {Portfolio} portfolio - The portfolio object containing holdings information.
 * @returns {AsyncResult<PortfolioData>} An async function that dispatches actions to fetch and process data,
 * and returns the portfolio data including funds, stocks, and benchmarks.
 */
export const getPortfolioHoldingsData =
  (portfolio: Portfolio): AsyncResult<PortfolioData> =>
  async (dispatch, getState) => {
    const [fundsHoldings, stocksHoldings, altHoldings, customYieldHoldings] = splitPortfolioHoldings(portfolio);
    const monthEndDataOption = getMonthEndDataOption(getState().comparison);

    const fundIds = fundsHoldings.filter(f => f.original.id != null).map(f => f.original.id) as string[];
    const stockIds = stocksHoldings.filter(s => s.original.id != null).map(s => s.original.id) as string[];
    const altIds = altHoldings.filter(a => a.original.id != null).map(a => a.original.id) as string[];

    const customYieldPromises = customYieldHoldings.map(holding => {
      if (!holding.original.id) {
        return null;
      }

      const shelfInfo = getShelfInfoForHolding(getState(), holding);

      return getCachedCustomYieldFund(holding.original.yieldParams!, shelfInfo, holding.currency, holding.original.id, monthEndDataOption);
    });

    const [funds, stocks, alts, customYieldResults] = await Promise.all([
      dispatch(getManyFundsData(fundIds)),
      dispatch(getManyStocksData(stockIds)),
      dispatch(getManyAltsData(altIds)),
      Promise.all(customYieldPromises), // Resolve all custom yield promises
    ]);

    const customYields = customYieldResults.filter(result => result != null) as Fund[];

    const calculatedYields = await Promise.all(customYields.map(f => dispatch(calculateCustomFund(f))));

    const [fundBenchmarks, stockBenchmarks, customYieldBenchmarks] = await Promise.all([
      dispatch(getFundsBenchmarks(funds)),
      dispatch(getStocksBenchmarks(stocks)),
      dispatch(getFundsBenchmarks(customYields)),
    ]);
    const formattedBenchmarks = stockBenchmarks.map(comparisonBenchmarkToBenchmark);
    return {
      funds: funds.concat(calculatedYields), // customYields uses the Fund model
      stocks,
      alts,
      benchmarks: [...fundBenchmarks, ...formattedBenchmarks, ...customYieldBenchmarks],
    };
  };

/**
 * Asynchronously retrieves a comparison item based on the current state and portfolio data.
 * This function returns a comparison item based on the current portfolio in state.edit-portfolio.
 *
 * This is used by the Conquest code to display the analysis graphs in the portfolio editor builder.
 * This is why we have all the options presetted, because it is a very specific context.
 *
 * @returns {AsyncResult<ComparisonItem>} A promise that resolves to a ComparisonItem.
 * @throws Will throw an error if the asynchronous operations fail.
 */
export const getComparisonItem = (): AsyncResult<ComparisonItem> => async (dispatch, getState) => {
  const state = getState();
  const { portfolio } = state.editPortfolio;

  const forcedMonthlyRolling = false; // this is about the trailing return, so nothing to do with the performance
  const useShortHistory = false; // we want the longest history possible
  const addIncomeStreamDisclaimer = false; // no use for us here

  const taxRate = selectCurrentTaxRate(state, portfolio.contact);
  const appRegion: AppRegion = state.auth.user != null ? state.auth.user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
  const data = await dispatch(getPortfolioHoldingsData(portfolio));

  const { funds, stocks, benchmarks, alts } = data;
  // We need to calculate the portfolio stats to get the allocations field, which is then set in portfolioToComparisonItem
  const newPortfolio = calculatePortfolioStats({
    portfolio,
    alts,
    funds,
    stocks,
    benchmarks,
    taxRate: taxRate || 0,
    showBaseCurrency: false,
    forcedMonthlyRolling,
    shortHistory: false,
    commonInception: undefined,
  });

  const options = {
    taxRate,
    shortHistory: useShortHistory,
  };

  return portfolioToComparisonItem(newPortfolio, appRegion, addIncomeStreamDisclaimer, data, options);
};

export const setPortfolioAllocationMode =
  (allocationMode: HoldingAllocationMode): Result<void> =>
  dispatch => {
    dispatch({ ...SET_PORTFOLIO_ALLOCATION_MODE, allocationMode });
  };

export const loadPortfolioForEdit =
  (
    portfolio: Portfolio,
    comparisonItem?: ComparisonItem,
    isPinned: boolean = false,
    isDirty?: boolean,
    isReadOnly: boolean = false,
    forceShowTaxDetails: boolean = false,
  ): AsyncResult<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      auth: { user },
    } = state;

    // Clear custom yield cache when loading a new portfolio
    clearCustomYieldCache();

    dispatch({ ...SET_PORTFOLIO_LOADING });
    await withLoaderAction(
      ADD_PORTFOLIOS_TO_COMPARISON.type,
      name => dispatch(startLoaderAction(name)),
      name => dispatch(stopLoaderAction(name)),
      async () => {
        let enhancedPortfolio = portfolio;

        if (portfolio.holdings.length) {
          const holdingsData = await dispatch(getPortfolioHoldingsData(portfolio));
          // If the portfolio contains holdings
          // Populate shelves information from appropriate store data
          // Step 1: Add shelves/compliance info
          const holdingsWithShelves = addShelvesToPortfolioHoldings(portfolio.holdings, holdingsData);
          // Step 2: Add instrument data (adding nav)
          const enrichedHoldings = mergeInstrumentDataIntoHoldings(holdingsWithShelves, holdingsData);

          enhancedPortfolio = {
            ...enhancedPortfolio,
            holdings: enrichedHoldings,
          };

          const isMissingHolding = helpers.portfolioHasMissingOrInvalidHoldings(portfolio);
          if (isMissingHolding) {
            enhancedPortfolio.holdings = addPortfolioHoldingsAllocations(enhancedPortfolio, holdingsData);
          }
        }

        enhancedPortfolio = setDefaultAllocationMode(enhancedPortfolio);
        const isTaxAnalysisEnabled = canAccessTaxAnalysis(user);
        const showTaxDetails =
          isTaxAnalysisEnabled && (forceShowTaxDetails || (enhancedPortfolio.holdings && enhancedPortfolio.holdings.some(h => h.taxSettings || h.lots)));

        dispatch(togglePortfolioEditor(true, isPinned, isPinned, isReadOnly));
        dispatch({ ...SET_PORTFOLIO, portfolio: enhancedPortfolio, comparisonItem, isDirty });
        dispatch(setPortfolioAllocationMode(enhancedPortfolio.allocationMode));

        if (showTaxDetails) {
          dispatch(setShowTaxDetails(true));

          // Consolidate holdings when forcing tax details on
          // processHoldingGroup handles region-specific logic (lots for US, merged for CA)
          if (forceShowTaxDetails) {
            await dispatch(consolidateTaxDetailsHoldings());
          }
        }
      },
    );
  };

export const setPortfolioIsCalculating =
  (isCalculating: boolean, promise?: Nullable<CancellablePromise<PortfolioCalculationPromiseReturn>>): Result<void> =>
  async dispatch => {
    dispatch({ type: SET_PORTFOLIO_IS_CALCULATING.type, isCalculating, calculationPromise: promise });
  };

export const setPortfolioCanSave =
  (canSave: boolean): Result<void> =>
  async dispatch => {
    dispatch({ type: SET_PORTFOLIO_CAN_SAVE.type, canSave });
  };

export const calculatePortfolio =
  (originalItem?: ComparisonItem, reallocate?: boolean): AsyncResult<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      editPortfolio: { portfolio, calculationPromise },
      comparison: { activeComparison },
      auth: { user },
    } = state;

    calculationPromise?.cancel();

    const promise = createCancellablePromise<PortfolioCalculationPromiseReturn>(
      new Promise(async (resolve, reject) => {
        try {
          let taxRate = 0;
          if (portfolio?.contact) {
            taxRate = selectCurrentTaxRate(state, portfolio?.contact);
          }

          const comparison = getCurrentStateComparison(state.comparison);
          const portfolioHoldingsData = await dispatch(getPortfolioHoldingsData(portfolio));

          const newPortfolio = dispatch(
            populatePortfolioMetrics(portfolio, portfolioHoldingsData, {
              showLongerHistory: comparison && comparison.shortHistory,
              taxRate,
              reallocate,
              commonInception: activeComparison?.commonInception,
            }),
          );

          const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
          const validateIncomeStream = user != null && userHasPermission(user, PERMISSIONS.INCOME_STREAM);
          const options = {
            taxRate,
            shortHistory: comparison && comparison.shortHistory,
            comparison,
          };
          let comparisonItem = portfolioToComparisonItem(newPortfolio, appRegion, validateIncomeStream, portfolioHoldingsData, options);
          const forcedMonthlyRolling: boolean = comparison != null ? shouldUseMonthlyRollingInReturns(comparison, user) : false;
          comparisonItem = await recalcPortfolioItem(
            dispatch,
            state,
            comparisonItem,
            forcedMonthlyRolling,
            comparison?.shortHistory,
            comparison?.showBaseCurrencies,
            true,
            comparison?.commonInception,
          );

          comparisonItem.color = originalItem && originalItem.color;
          comparisonItem.providedBy = originalItem && originalItem.providedBy;
          comparisonItem.isExpanded = originalItem && originalItem.isExpanded;
          comparisonItem.isFX = originalItem && originalItem.isFX;
          comparisonItem.scenarios = originalItem && originalItem.scenarios;

          comparisonItem.holdings.forEach((comparisonHolding: ComparisonHolding) => {
            const holding = [...portfolioHoldingsData.funds, ...portfolioHoldingsData.stocks, ...portfolioHoldingsData.alts].find(
              item => item._id === comparisonHolding.id,
            );
            comparisonHolding.rolling = holding && holding.performance && holding.performance.rolling;
          });

          return resolve({
            newPortfolio,
            comparisonItem,
          });
        } catch (err) {
          return reject({ errorKey: 'PORTFOLIO_CALCULATION_FAILED' });
        }
      }),
    );

    promise.promise
      .then((res: { newPortfolio: any; comparisonItem: any }) => {
        dispatch({
          type: `${CALCULATE_PORTFOLIO.type}_SUCCESS`,
          portfolio: res.newPortfolio,
          comparisonItem: res.comparisonItem,
          refreshHoldings: reallocate,
        });

        dispatch(setPortfolioIsCalculating(false));
      })
      .catch((err: ApiError) => {
        throw err;
      });

    dispatch(setPortfolioIsCalculating(true, promise));
  };

export const populatePortfolioMetrics =
  (portfolio: Portfolio, data: PortfolioData, options: CalculatePortfolioOptions = {}): Result<Portfolio> =>
  (dispatch, getState) => {
    const state = getState();
    const {
      comparison: comparisonState,
      auth: { user },
    } = state;

    const { alts, funds, stocks, benchmarks } = data;

    let newPortfolio: Portfolio | undefined = portfolio;
    const instrumentData = [...alts, ...funds, ...stocks];
    if (options.reallocate) {
      newPortfolio = recalculatePortfolioAllocations(portfolio, instrumentData);
    }

    const comparison = getCurrentStateComparison(comparisonState);

    const { commonInception, taxRate = 0 }: { commonInception?: CommonInception; taxRate?: number } = options;
    const forcedMonthlyRolling: boolean = comparison != null ? shouldUseMonthlyRollingInReturns(comparison, user) : false;
    newPortfolio = calculatePortfolioStats({
      portfolio: newPortfolio,
      alts,
      funds,
      stocks,
      benchmarks,
      taxRate,
      showBaseCurrency: comparison?.showBaseCurrencies || false,
      forcedMonthlyRolling,
      shortHistory: false,
      commonInception,
    });

    // Convert to comparisonItem to use in GrowthGraph
    if (newPortfolio.performance) {
      const holdings = [...deepClone(funds), ...deepClone(stocks), ...deepClone(alts)];
      for (const holding of holdings) {
        if (holding.currency === CURRENCY.USD && holding.performance.rollingUSD && holding.performance.percentGrowthUSD) {
          holding.performance.rolling = holding.performance.rollingUSD;
          holding.performance.percentGrowth = holding.performance.percentGrowthUSD;
          holding.performance.calendarYear = holding.performance.calendarYearUSD;
        }
      }
      const compHoldings = portfolioHoldingsToComparisonHoldings(portfolio.holdings, instrumentData);
      const monthlyRollings = rollingPerformance(compHoldings, Interval.Monthly, portfolio.discretionaryMer, undefined, options.showLongerHistory);
      const performanceBase = calculatePerformance(portfolio, holdings, monthlyRollings, taxRate, forcedMonthlyRolling);
      newPortfolio.performanceBase = { ...newPortfolio.performance, ...performanceBase };
    }

    return newPortfolio;
  };

export const setPortfolioContact =
  (contact: GenericContact): Result<void> =>
  dispatch => {
    dispatch({ type: SET_PORTFOLIO_CONTACT.type, contact });
  };

export const removePortfolioPlanAccount = (): Result<void> => dispatch => {
  dispatch({ type: REMOVE_PORTFOLIO_PLAN_ACCOUNT.type });

  // Reset the risk profile when removing the plan account
  dispatch(deletePortfolioRiskProfile());
};

export const setPortfolioPlanAccount =
  (planId: string, planAccountId: string, planAccount?: Account): Result<void> =>
  async (dispatch, getState) => {
    dispatch({
      type: SET_PORTFOLIO_PLAN_ACCOUNT.type,
      payload: {
        planId,
        planAccountId,
        planAccount,
      },
    });
  };

export const setPortfolioEnterprise = (): Result<void> => (dispatch, getState) => {
  const {
    enterprise: { details },
  } = getState();
  if (details) {
    dispatch({ type: SET_PORTFOLIO_ENTERPRISE.type, enterpriseId: details._id });
  }
};

export const addFundsToPortfolio =
  (funds: Fund[]): AsyncResult<void[]> =>
  async dispatch => {
    dispatch(startLoaderAction(ADD_FUNDS_TO_PORTFOLIO.type));
    await dispatch(cacheFundsData(funds));
    const promises = funds.map(f => dispatch(addFundToPortfolioById(f._id)));
    return Promise.all(promises).finally(() => {
      dispatch(stopLoaderAction(ADD_FUNDS_TO_PORTFOLIO.type));
    });
  };

export const registerOnPortfolioCancel =
  (handler: () => void): Result<void> =>
  dispatch => {
    dispatch({ ...REGISTER_ON_CANCEL, handler });
  };

export const cancelPortfolioEditing = (): Result<void> => (dispatch, getState) => {
  getState().editPortfolio.onCancel();
};

export const setComparisonItemName =
  (name: string): Result<void> =>
  dispatch =>
    dispatch({ ...SET_COMPARISON_ITEM_NAME, name });

export const setExpandComparisonItem =
  (isExpanded: boolean): Result<void> =>
  (dispatch, getState) => {
    const { comparisonItem } = getState().editPortfolio;

    if (!comparisonItem || isExpanded === comparisonItem.isExpanded) {
      return;
    }

    const newItem = { ...comparisonItem, isExpanded };
    dispatch({ ...SET_COMPARISON_ITEM, item: newItem });
  };

export const attachProfileToPortfolio =
  (profile: Partial<PortfolioRiskProfile>): Result<void> =>
  dispatch => {
    dispatch(startLoaderAction(SET_IS_ATTACHING_PROFILE_TO_PORTFOLIO.type));
    dispatch({ ...SET_RISK_PROFILE, profile });
    dispatch(stopLoaderAction(SET_IS_ATTACHING_PROFILE_TO_PORTFOLIO.type));
  };

export const clearProductAdded = (): Result<void> => dispatch => {
  dispatch({ ...CLEAR_PRODUCT_ADDED });
};

export const deletePortfolioRiskProfile = (): Result<void> => dispatch => {
  dispatch({ ...SET_RISK_PROFILE, undefined });
};

export const setHoldingToReplace =
  (holdingToReplace?: PortfolioHolding): Result<void> =>
  dispatch => {
    dispatch({ type: SET_HOLDING_TO_REPLACE.type, holdingToReplace });
  };
export const setHoldingToCompareComplianceAlts =
  (holdingToCompare?: PortfolioHolding): Result<void> =>
  dispatch => {
    dispatch({ type: SET_HOLDING_TO_COMPARE_COMPLIANCE_ALTS.type, holdingToCompare });
  };

export const setPortfolioContentOption =
  (option: PortfolioBuilderContentOption): Result<void> =>
  dispatch => {
    dispatch({ ...SET_PORTFOLIO_CONTENT_OPTION, option });
  };

export const saveModelCopyToDraftPortfolio =
  (portfolio: Portfolio): AsyncResult<Portfolio> =>
  (dispatch, getState) => {
    const { current } = getState().contacts;

    if (current != null) {
      const localPortfolio = deepClone(portfolio);
      localPortfolio._id = generateTempId();
      localPortfolio.correlationId = localPortfolio._id.replace('new-', '');
      localPortfolio.mine = false;
      localPortfolio.enterprise = undefined;
      localPortfolio.enterpriseGroups = undefined;
      localPortfolio.contact = undefined;
      localPortfolio.name = {
        [LOCALE.EN_CA]: `${formatMessageWithLocale('Portfolios.CopyPrefix', LOCALE.EN_CA, { number: '' })} ${portfolio.name[LOCALE.EN_CA]}`,
        [LOCALE.FR_CA]: `${formatMessageWithLocale('Portfolios.CopyPrefix', LOCALE.FR_CA, { number: '' })} ${portfolio.name[LOCALE.FR_CA]}`,
      };

      if (localPortfolio.riskProfile) {
        // The risk profile most likely already has an id - since we're copying the portfolio,
        // we also need to create a fresh, local risk profile. Setting the id to undefined
        // tells the API to create a new risk profile upon saving.
        localPortfolio.riskProfile._id = undefined;
      }

      return dispatch(savePortfolio(localPortfolio)).then(savedPortfolio => savedPortfolio);
    }

    return Promise.reject();
  };

export const batchAddHoldingsToPortfolio =
  (instruments: Array<PortfolioHolding>): Result<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(BATCH_ADD_HOLDINGS_TO_PORTFOLIO.type));

    dispatch({ type: SET_PORTFOLIO_ALLOCATION_MODE.type, allocationMode: HoldingAllocationMode.Units });

    const { portfolio } = getState().editPortfolio;

    const holdings = instruments.map(instrument => ({
      ...instrument,
      _id: generateBSONId(),
    }));

    const newPortfolio: Portfolio = {
      ...portfolio,
      holdings,
    };

    const holdingsData = await dispatch(getPortfolioHoldingsData(newPortfolio));

    newPortfolio.holdings = addPortfolioHoldingsAllocations(newPortfolio, holdingsData);

    // Recalculate portfolio size
    const size = round(newPortfolio.holdings.map(holding => holding.dollars).reduce(sum, 0), 2);

    // Recalculate weight
    newPortfolio.holdings = calculateHoldingWeightsFromDollars(newPortfolio.holdings, size);

    dispatch({ type: SET_PORTFOLIO_HOLDINGS.type, holdings: newPortfolio.holdings, size });
    dispatch(stopLoaderAction(BATCH_ADD_HOLDINGS_TO_PORTFOLIO.type));
  };

export const addHoldingsToPortfolioFromCsv =
  (payload: { instruments: Array<CsvFormat>; lotsEnabled?: boolean }): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch(startLoaderAction(ADD_HOLDINGS_TO_PORTFOLIO_FROM_CSV.type));

    const {
      data: { instruments },
    }: { data: { instruments: Array<any> } } = await api.post(FETCH_HOLDINGS, null, payload);

    await dispatch(batchAddHoldingsToPortfolio(instruments));
    dispatch(stopLoaderAction(ADD_HOLDINGS_TO_PORTFOLIO_FROM_CSV.type));
  };

export const addPortfolioMetrics =
  (portfolio: Portfolio): AsyncResult<Portfolio> =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      contacts: { current },
    } = state;

    let taxRate = 0;
    if (current) {
      taxRate = selectCurrentTaxRate(state, portfolio.contact);
    }

    // TODO: Might need to add common inception here for the risk metrics later
    const options = { taxRate };

    const data = await dispatch(getPortfolioHoldingsData(portfolio));
    const newPortfolio = dispatch(populatePortfolioMetrics(portfolio, data, options));
    return newPortfolio;
  };

export const transformFDC3ToCapIntelPortfolio =
  (fdcPortfolio: PortfolioFDC3): AsyncResult<Portfolio> =>
  async (dispatch, getState) => {
    try {
      // Retrieve the current Contact
      const { contacts, auth } = getState();
      const customProxyMapping = auth.user?.appConfig?.customProxyMapping;

      const capIntelPortfolio = await transformFDC3Portfolio(fdcPortfolio, true, customProxyMapping);

      const contact = contacts.current?.contact;

      if (contact) {
        capIntelPortfolio.contact = contact;
      }

      if (!contact && auth.user?.appConfig.mode === AppMode.CONTACT) {
        const postMessagePortfolio = contacts?.postMessagePortfolios.find(c => c._id === capIntelPortfolio._id);
        const postMessageContact = postMessagePortfolio?.contact;
        capIntelPortfolio.contact = postMessageContact;
      }

      return capIntelPortfolio;
    } catch (err) {
      if (err instanceof Error) {
        sentryUtils.captureError(err);
      }
      return Promise.reject({ key: 'DEFAULT_ERROR_TEST' });
    }
  };

export const setPortfolioEditorContext =
  (editorContext: PortfolioEditorContext): Result<void> =>
  dispatch => {
    dispatch({ type: SET_PORTFOLIO_EDITOR_CONTEXT.type, editorContext });
  };

export const setPortfolioEditorIsCreatingModel =
  (isCreatingModel?: boolean): Result<void> =>
  dispatch => {
    dispatch({ type: SET_PORTFOLIO_EDITOR_IS_CREATING_MODEL.type, isCreatingModel: isCreatingModel || false });
  };

export const togglePortfolioValueLocked = (): Result<void> => dispatch => {
  dispatch({ type: TOGGLE_PORTFOLIO_VALUE_LOCKED.type });
};

export const removeTaxInfoFromHoldings = (holdings: PortfolioHolding[]): PortfolioHolding[] => {
  return holdings.map(holding => {
    const { taxSettings, lots, ...holdingWithoutTax } = holding;
    return holdingWithoutTax;
  });
};

export const setShowTaxDetails =
  (show: boolean): AsyncResult<void> =>
  async (dispatch, getState) => {
    dispatch({ type: SET_SHOW_TAX_DETAILS.type, payload: show });

    // If toggling ON, consolidate holdings according to region-specific tax logic
    if (show) {
      await dispatch(consolidateTaxDetailsHoldings());
    }
  };

export const removeTaxDetailsFromPortfolio = (): AsyncResult<void> => async (dispatch, getState) => {
  const { portfolio } = getState().editPortfolio;

  if (portfolio.holdings.some(h => h.lots?.length || h.taxSettings)) {
    const cleanedHoldings = removeTaxInfoFromHoldings(portfolio.holdings);

    dispatch({
      type: SET_PORTFOLIO_HOLDINGS.type,
      holdings: cleanedHoldings,
    });

    await dispatch(recalculatePortfolioAllocation());
  }
};

export const groupHoldingsByOriginalId = (
  holdings: PortfolioHolding[],
): { byOriginalId: Map<string, PortfolioHolding[]>; nonMergeable: PortfolioHolding[] } => {
  const byOriginalId = new Map<string, PortfolioHolding[]>();
  const nonMergeable: PortfolioHolding[] = [];

  for (const holding of holdings) {
    const originalId = holding.original?.id;

    if (!originalId) {
      nonMergeable.push(holding);
      continue;
    }

    const existing = byOriginalId.get(originalId);
    if (existing) {
      existing.push(holding);
    } else {
      byOriginalId.set(originalId, [holding]);
    }
  }

  return { byOriginalId, nonMergeable };
};

export function isTaxableHolding(holding: PortfolioHolding, instrument?: Nullable<Holding>): boolean {
  if (!instrument) return false;
  const assetType = (instrument as Fund | undefined)?.assetType;
  return isCapitalGainsTaxableHolding(holding.original.type, assetType, holding.nav);
}

export const convertHoldingsToLotStructure = (holdings: PortfolioHolding[]): PortfolioHolding => {
  const baseHolding = holdings[0];
  const lots = holdings.map(h => ({
    _id: generateBSONId(), // because the first lot uses the same ID as the parent holding, we need to account for the first one
    units: h.units ?? 0,
    dollars: h.dollars ?? 0,
    costBasis: h.taxSettings?.costBasis ?? 0,
    purchaseDate: h.taxSettings?.purchaseDate,
  }));

  const totalUnits = lots.reduce((sum, lot) => sum + lot.units, 0);
  const totalDollars = lots.reduce((sum, lot) => sum + lot.dollars, 0);

  return {
    ...baseHolding,
    units: totalUnits,
    dollars: totalDollars,
    lots,
  };
};

export const convertHoldingsToMergedStructure = (holdings: PortfolioHolding[]): PortfolioHolding => {
  const baseHolding = holdings[0];
  const mergedHoldings = holdings.map(h => ({
    ...h,
    costBasis: h.taxSettings?.costBasis ?? 0,
    dollars: h.dollars ?? 0,
    units: h.units ?? 0,
    weight: h.weight ?? 0,
  }));

  const totalDollars = mergedHoldings.reduce((sum, holding) => sum + holding.dollars, 0);
  const totalUnits = mergedHoldings.reduce((sum, holding) => sum + holding.units, 0);
  const totalWeight = mergedHoldings.reduce((sum, holding) => sum + holding.weight, 0);

  return {
    ...baseHolding,
    dollars: totalDollars,
    units: totalUnits,
    weight: totalWeight,
  };
};

export const processHoldingGroup = (holdings: PortfolioHolding[], allInstruments: Holding[], user?: Nullable<User>): PortfolioHolding[] => {
  const baseHolding = holdings[0];
  const originalId = baseHolding.original?.id;
  const instrument = allInstruments.find(i => i._id === originalId);
  const isRegionCA = validateUserAppRegion(user, AppRegion.CAN);
  const isInLotRegion = isUserInLotRegion(user);

  // Canadian region: use merged holdings structure for tax tracking
  if (isRegionCA) {
    if (!isTaxableHolding(baseHolding, instrument)) {
      return holdings;
    }
    return [convertHoldingsToMergedStructure(holdings)];
  }

  // US region: use lot-based structure for tax tracking
  if (isInLotRegion) {
    // Preserve existing lot structure to prevent data loss
    const hasExistingLotsInGroup = holdings.some(h => h.lots !== undefined && h.lots.length > 0);
    if (hasExistingLotsInGroup) {
      return holdings;
    }

    if (!isTaxableHolding(baseHolding, instrument)) {
      return holdings;
    }
    return [convertHoldingsToLotStructure(holdings)];
  }

  // Other regions: no tax consolidation logic applies
  return holdings;
};

/**
 * Consolidates portfolio holdings based on tax analysis requirements.
 * For US region: Creates lot-based structure for capital gains tracking.
 * For CA region: Merges holdings with same instrument into consolidated structure.
 *
 * This function handles both region-specific flows since processHoldingGroup
 * contains the region-specific logic internally.
 */
export const consolidateTaxDetailsHoldings = (): AsyncResult<void> => async (dispatch, getState) => {
  const state = getState();
  const { portfolio, showTaxDetails } = state.editPortfolio;
  const { user } = state.auth;
  const isTaxAnalysisFeatureEnabled = canAccessTaxAnalysis(user);

  // Early exit if prerequisites aren't met
  if (!isTaxAnalysisFeatureEnabled || !showTaxDetails) {
    return;
  }

  // Batch-fetch all holding data
  const holdingsData = await dispatch(getPortfolioHoldingsData(portfolio));
  const allInstruments = [...holdingsData.funds, ...holdingsData.stocks, ...holdingsData.alts];

  // Merge nav data into holdings before processing
  // When holdings are loaded from CSV or toggled between tax views, they may not have nav data yet.
  // The isTaxableHolding check requires nav data to determine if a holding is taxable.
  // Without this merge, hasNavData would always be false and valid holdings wouldn't be converted to lots.
  const holdingsWithNav = mergeInstrumentDataIntoHoldings(portfolio.holdings, holdingsData);

  // Group holdings by original.id
  const { byOriginalId, nonMergeable } = groupHoldingsByOriginalId(holdingsWithNav);

  // Process each group with region-specific logic (handled in processHoldingGroup)
  const mergedHoldings = flatMap(Array.from(byOriginalId.values()), holdings => processHoldingGroup(holdings, allInstruments, user));

  // Combine processed holdings with non-mergeable ones
  const newHoldings = [...mergedHoldings, ...nonMergeable];

  dispatch({
    type: SET_PORTFOLIO_HOLDINGS.type,
    holdings: newHoldings,
  });
};
